import { useCallback } from 'react';
import {
  presign,
  uploadToS3,
  confirm,
  validateImageFile,
  type MediaCategory,
} from '@/lib/upload';

/**
 * 상품 상세 본문(detailHtml) 에디터 이미지 업로드용 presign 카테고리.
 * 상세 이미지 갤러리와 동일한 PRODUCT_DETAIL 버킷을 사용한다(be-catalog 미디어 서비스 수용값).
 */
const PRODUCT_DETAIL_IMAGE_CATEGORY: MediaCategory = 'PRODUCT_DETAIL';

/**
 * RichTextEditor 의 onUploadImage 에 그대로 넘길 수 있는 업로드 함수를 반환한다.
 * presign → S3 직접 PUT → confirm 3단계를 거쳐 publicUrl 을 돌려준다.
 * 클라이언트 선검증 실패/네트워크 오류는 한국어 메시지로 throw 하므로
 * 호출부(RichTextEditor onUploadError)가 표면화한다.
 *
 * cms 의 useEventImageUpload 와 동일 패턴(presign 3단계)이며 카테고리만 PRODUCT_DETAIL.
 * 상품 다국어 상세 본문 탭(TranslationsTabs)의 모든 로케일 에디터가 공유한다.
 */
export const useProductDetailImageUpload = (): ((file: File) => Promise<string>) => {
  return useCallback(async (file: File): Promise<string> => {
    const validationError = validateImageFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const [slot] = await presign([{ file, category: PRODUCT_DETAIL_IMAGE_CATEGORY }]);
    if (!slot) {
      throw new Error('업로드 URL 발급 응답이 올바르지 않습니다.');
    }

    await uploadToS3([{ item: slot, file }]);

    const confirmed = await confirm([slot.fileId]);
    const publicUrl = confirmed.get(slot.fileId) ?? slot.publicUrl;
    if (!publicUrl) {
      throw new Error('업로드한 이미지 주소를 확인하지 못했습니다.');
    }
    return publicUrl;
  }, []);
};
